import { ROLE } from './permission'

/**
 * Phạm vi của một trang trong nhóm "Quản lý tài khoản" > tài khoản ngoài.
 *
 * Một trang = một tập role. Bảng này là nguồn chân lý duy nhất cho: gửi khoá lọc
 * nào lên API, tiêu đề gì, hiện cột/panel nào, và tạo mới được role nào.
 *
 * 🔴 MỖI SCOPE CHỈ ĐƯỢC KHAI MỘT trong hai khoá `roleTags` / `withoutRoleTags`.
 * Backend (apps/user-service/src/repositories/user.repository.ts:39-53) dùng CHUNG
 * tên tham số `role_tags` cho cả nhánh IN lẫn nhánh NOT IN. TypeORM gộp tham số theo
 * tên nên lần set sau đè lần trước → query thành `IN (X) AND NOT IN (X)` → trả về
 * RỖNG mà không ném lỗi. Nhánh erp-v2-migrate chặn việc này bằng kiểu union của
 * TypeScript; nhánh này là JavaScript nên chặn bằng `buildRoleFilterParams` ném lỗi
 * cộng với test trong accountScope.test.js.
 */

export const ACCOUNT_SCOPES = {
  khdn: {
    key: 'khdn',
    listHeading: 'Danh sách tài khoản doanh nghiệp',
    roleTags: [ROLE.BUSINESS_CUSTOMER_EXTERNAL_ACCOUNT],
    pageRoleTags: [ROLE.BUSINESS_CUSTOMER_EXTERNAL_ACCOUNT],
    showAccountTypeColumn: true,
    showRoleFilterPanel: false,
  },
  agent: {
    key: 'agent',
    listHeading: 'Danh sách tài khoản đại lý',
    roleTags: [ROLE.AGENT_EXTERNAL_ACCOUNT],
    pageRoleTags: [ROLE.AGENT_EXTERNAL_ACCOUNT],
    // Trang chỉ có một loại tài khoản → cột thành hằng số, lọc theo loại là vô nghĩa.
    showAccountTypeColumn: false,
    showRoleFilterPanel: false,
  },
  ctv: {
    key: 'ctv',
    listHeading: 'Danh sách tài khoản CTV',
    // Lọc bằng "trừ ra" chứ không phải "là CTV": giữ tính chất hợp của ba trang phủ
    // kín 100% tài khoản ngoài, nên tài khoản Khách hàng bán lẻ (hiện 0 dòng, role
    // vẫn sống và vẫn chọn được ở form tạo) không bị tàng hình.
    withoutRoleTags: [
      ROLE.BUSINESS_CUSTOMER_EXTERNAL_ACCOUNT,
      ROLE.AGENT_EXTERNAL_ACCOUNT,
    ],
    pageRoleTags: [
      ROLE.COLLABORATOR_EXTERNAL_ACCOUNT,
      ROLE.RETAIL_CUSTOMER_EXTERNAL_ACCOUNT,
    ],
    showAccountTypeColumn: true,
    showRoleFilterPanel: true,
  },
}

/**
 * Dựng đúng MỘT khoá lọc role cho query string của
 * `GET /api/v1/accounts/external-accounts`.
 *
 * Ném lỗi thay vì trả về im lặng: khai sai ở đây làm API trả rỗng mà không có dấu
 * hiệu gì, rất khó lần ra từ giao diện.
 */
export const buildRoleFilterParams = (scope) => {
  const hasInclude = Array.isArray(scope?.roleTags)
  const hasExclude = Array.isArray(scope?.withoutRoleTags)

  if (hasInclude && hasExclude) {
    throw new Error(
      `accountScope "${scope?.key}" khai cả hai khoá roleTags và withoutRoleTags. `
      + 'Backend dùng chung tên tham số nên gửi cả hai sẽ trả về rỗng. Chỉ khai một.',
    )
  }
  if (!hasInclude && !hasExclude) {
    throw new Error(
      `accountScope "${scope?.key}" không khai khoá lọc role nào `
      + '(cần roleTags hoặc withoutRoleTags).',
    )
  }

  return hasInclude
    ? { role_tags: scope.roleTags.join(',') }
    : { without_role_tags: scope.withoutRoleTags.join(',') }
}

/** `role_tag` từ API là chuỗi, so khớp với danh sách role của trang. */
export const isRoleTagInScope = (scope, roleTag) => (
  (scope?.pageRoleTags || []).includes(roleTag)
)

/**
 * Role cho ô "Loại tài khoản" ở màn CHI TIẾT.
 * Giữ nguyên luật cũ (KHDN một rổ, còn lại một rổ) để không thu hẹp quyền đổi loại
 * tài khoản của super/managing/finance admin. `scope` có thể undefined (màn "Hồ sơ của tôi").
 */
export const getDetailRoles = (scope, roles) => (
  scope?.key === 'khdn'
    ? (roles || []).filter((x) => x.role_tag === ROLE.BUSINESS_CUSTOMER_EXTERNAL_ACCOUNT)
    : (roles || []).filter((x) => x.role_tag !== ROLE.BUSINESS_CUSTOMER_EXTERNAL_ACCOUNT)
)

/** Role cho ô "Loại tài khoản" ở màn TẠO MỚI: đúng phạm vi trang. */
export const getCreateRoles = (scope, roles) => (
  (roles || []).filter((x) => isRoleTagInScope(scope, x.role_tag))
)
